import api from '~/utils/api'
import { createGrowthEventId } from '~/utils/growthEvents'
import { readPublicMusicList } from '~/utils/public-music-list.mjs'
import { sectionMap, getSectionById } from '~/services/musicSectionMapConfig'
import { musicService, type MusicRank } from '~/services/musicService'
import type { PlayerTrack } from '~/stores/player'
import type { PlaybackContext } from '~/services/songPlayback'
import { rankingPath, readableRanking } from './ranking-contract.mjs'
import { isLegacyRankingUnsupported } from './discovery-source.mjs'
import type { CompetitionSnapshot } from './competition-types'

export type DiscoverySource = 'creator' | 'catalog' | 'editor_pick'
// B1 fill-source fields; absent on older servers (creator, count visible).
export type ListSong = PlayerTrack & { rank?: number; rank_delta?: number; is_new?: boolean; recently_published?: boolean; source?: DiscoverySource; play_count_visible?: boolean; display_only?: boolean; rank_is_ordinal?: boolean; award_rank?: number; award_eligible?: boolean; champion?: boolean; listContext?: PlaybackContext }
export interface RankingSummary extends CompetitionSnapshot { ranking_key: string; title_key: string; window_end: number; total: number; top_ids: string[]; fill_window?: '24h' | '72h' | '7d'; catalog_filled?: boolean }
export type HomeRanking = MusicRank & Partial<RankingSummary> & { top_songs: ListSong[]; load_error?: boolean }
// All base/language charts, in the prototype's order, precede any additional charts.
export const HOME_CHART_ORDER = ['global-trending', 'new-songs', 'english-trending', 'spanish-trending', 'korean-trending', 'japanese-trending', 'chinese-trending', 'french-trending', 'german-trending']
const chartOrder = (path: string) => HOME_CHART_ORDER.includes(path) ? HOME_CHART_ORDER.indexOf(path) : HOME_CHART_ORDER.length
const options = { timeout: 6500, headers: { 'Content-Type': 'application/json' }, musicmintInlineError: true }

export async function rankingSummary(locale: string): Promise<RankingSummary[]> {
  const response = await api.post('music/public/v1/rankings/summary', { locale }, options)
  if (response?.code !== 200 || !Array.isArray(response.data?.items)) throw Object.assign(new Error('Rankings unavailable'), { status: response?.code })
  return response.data.items
}
export async function rankingPage(key: string, page: number, size = 20, locale = 'en') {
  const requestId = createGrowthEventId()
  const response = await api.post('music/public/v1/rankings/music', { ranking_key: key, locale, page, size }, options)
  if (response?.code !== 200 || !Array.isArray(response.data?.items)) throw Object.assign(new Error('Ranking unavailable'), { status: response?.code })
  return { ...response, data: { ...response.data, list: response.data.items.map((song: ListSong, index: number) => ({ ...song, listContext: { requestId, position: index + 1 } })) } }
}
export function attributeLegacy(response: any) {
  const requestId = createGrowthEventId()
  const list = readPublicMusicList(response).map((song: ListSong, index: number) => ({ ...song, listContext: { requestId, position: index + 1 } }))
  return { ...response, data: { ...(Array.isArray(response.data) ? {} : response.data), list }, total: response.data?.total ?? response.total }
}
export async function homeRankings(locale: string, legacy: () => Promise<unknown>) {
  let summary: RankingSummary[]
  try { summary = await rankingSummary(locale) } catch (cause) {
    // Only an old server (404/426) falls back; network/5xx surfaces as the retryable error state.
    if (!isLegacyRankingUnsupported(cause)) throw cause
    return legacyHomeRankings(locale, legacy)
  }
  // The server omits small sections from the summary; never re-add them here.
  // An empty summary is a real empty result, not a reason to read legacy endpoints.
  // Bounded concurrency: the summary can contain dozens of genre charts.
  let cursor = 0
  const slots: HomeRanking[] = new Array(summary.length)
  await Promise.all(Array.from({ length: Math.min(4, summary.length) }, async () => {
    while (cursor < summary.length) {
      const index = cursor++, item = summary[index]
      let songs: ListSong[] = []
      let loadError = false;
      let detail: CompetitionSnapshot = {}
      try {
        const data = (await rankingPage(item.ranking_key, 1, 6, locale)).data
        songs = data.list.slice(0, 6)
        detail = { status: data.status ?? item.status, challenge: data.challenge, last_awarded: data.last_awarded, settlement_day: data.settlement_day ?? item.settlement_day }
      } catch { loadError = true; }
      slots[index] = { ...item, ...detail, id: item.ranking_key, section_id: item.ranking_key, selected_option: '', name: readableRanking(item.ranking_key), cover_url: '', count: item.total, created: item.window_end, top_songs: songs.slice(0, 6), load_error: loadError && !songs.length }
    }
  }))
  return { code: 200, data: slots }
}
// Old servers without the V3 summary endpoint (404/426).
async function legacyHomeRankings(locale: string, legacy: () => Promise<unknown>) {
  let fallback: Promise<MusicRank[]> | undefined
  const old = () => fallback ||= legacy().then(value => readPublicMusicList(value) as MusicRank[])
  const sections = [...sectionMap].sort((a, b) => chartOrder(a.path) - chartOrder(b.path))
  const slots: HomeRanking[] = new Array(sections.length)
  let cursor = 0
  await Promise.all(Array.from({ length: 4 }, async () => {
    while (cursor < sections.length) {
      const index = cursor++, section = sections[index]
      let songs: ListSong[] = []
      let loadError = false
      try { songs = attributeLegacy(await musicService.getMoreChartMusic(section.id, section.selected_option, { page: 1, size: 6 }, options)).data.list.slice(0, 6) } catch { loadError = true }
      if (!songs.length) {
        try { songs = attributeLegacy({ code: 200, data: (await old()).find(chart => getSectionById(chart.section_id, 'rank', chart.selected_option)?.path === section.path)?.top_songs || [] }).data.list.slice(0, 6) } catch { /* Render the real empty state. */ }
      }
      slots[index] = { id: section.path, section_id: section.id || section.path, selected_option: section.selected_option || '', name: section.title, cover_url: '', count: songs.length, created: 0, top_songs: songs.slice(0, 6), load_error: loadError && !songs.length }
    }
  }))
  // Keep new charts visible even while only the legacy summary is available.
  const dedicated = await dedicatedPreviews(locale)
  return { code: 200, data: [...slots, ...dedicated] }
}
async function dedicatedPreviews(locale: string): Promise<HomeRanking[]> {
  return Promise.all(['ops_featured', 'music_creator_new'].map(async key => {
    let response: any;
    let loadError = false;
    try { response = await rankingPage(key, 1, 6, locale) } catch { loadError = true; }
    return { ranking_key: key, title_key: `ranking.${key}`, id: key, section_id: key, selected_option: '', name: readableRanking(key), cover_url: '', count: response?.data.total || 0, total: response?.data.total || 0, created: response?.data.window_end || 0, window_end: response?.data.window_end || 0, top_ids: [], top_songs: response?.data.list || [], load_error: loadError };
  }));
}
export function homeRankingPath(chart: HomeRanking) {
  return chart.ranking_key ? `/music/${rankingPath(chart.ranking_key)}` : `/music/${getSectionById(chart.section_id, 'rank', chart.selected_option)?.path || 'global-trending'}`
}
export function legacyChartLinks(items: RankingSummary[]) {
  const paths = new Set(items.map(item => rankingPath(item.ranking_key)))
  return sectionMap.filter(section => !paths.has(section.path))
}
